import { ICallRequest } from 'dialer/classes/models/ICallRequest'
import { getEmptySearchResult, ISearchResult } from 'dialer/classes/models/IDialerState'
import { IPagination } from 'dialer/classes/models/models'
import { checkIsFailMessages } from 'dialer/helpers/messages'
import { getClientSecret, getTokens } from 'src/core/classes/helpers/StorageHelper'
import { EWSEvent, IWSClient } from 'src/core/classes/ws/IWSClient'
import { TResponse } from 'src/core/classes/ws/WebSocketClient'
import { wsConnectionPool } from 'src/core/classes/ws/WSConnectionPool'
import logger from 'src/core/utils/Logger'

import { MY_CONNECT_TYPE } from '../../models/MyConnectType'

export type TDialerSearchPayload = {
  input: string
  type: MY_CONNECT_TYPE
  countryCodeList: string[]
  showMyPartners: number
  onlyFavourite: number
  searchHash?: string
  pagination?: IPagination
}

type TElasticSearchItemClickedPayload = {
  elasticSearchID: string
  clickedLink: string
  numberInResult: number
  callRequest: ICallRequest
}

class DialerApi {
  private readonly serviceName = 'dialer'
  public async addListeners() {
    wsConnectionPool.addListener(this.serviceName, EWSEvent.open, async (client) => {
      const [accessToken] = getTokens()
      if (accessToken) {
        const [clientId] = getClientSecret()
        await dialerAPI.authorizeByToken(client, accessToken, clientId)
      }
    })
  }

  public authorizeByToken = async (client: IWSClient, token: string, sessionID: string | null) => {
    if (token) {
      const response = await client.call('auth', 'token', { token, sessionID }, true)
      if (response.status === 'success') {
        client.isAuthorized.resolve()
      } else {
        throw new Error('Was not able to authorize')
      }
    }
  }

  public search = async (payload: TDialerSearchPayload) => {
    let result
    try {
      const wsClient = await wsConnectionPool.getAuthorizedClient(this.serviceName)
      const response = await wsClient.call<TResponse<ISearchResult>>('search', 'search', payload)
      result = { success: response.status === 'success', data: response.data }
    } catch (e) {
      result = { success: false, data: getEmptySearchResult() }
    }

    return result
  }

  public call = async (
    payload: ICallRequest
  ): Promise<{
    success: boolean
    errorMessage?: string
    errorCodes?: string[]
  }> => {
    const wsClient = await wsConnectionPool.getAuthorizedClient(this.serviceName)
    const result = await wsClient.call('my-connect', 'call', payload, true)

    const isError = ['error', 'fail'].includes(result.status)
    if (isError) {
      let messages: string[] = []
      const codes: string[] = []
      if (result.status === 'fail') {
        const failMessages = result.data?.messages || ['Unknown error']

        if (checkIsFailMessages(failMessages)) {
          for (const message of failMessages) {
            if (message.message) {
              messages.push(message.message)
            }

            if (message.code) {
              codes.push(message.code)
            }
          }
        } else {
          messages = failMessages
        }
      }

      if (result.status === 'error') {
        if (result.message) {
          messages.push(result.message)
        }

        codes.push(result.code)
      }

      return {
        success: false,
        errorMessage: messages.length ? messages.join(', ') : '',
        errorCodes: codes,
      }
    }
    return {
      success: true,
    }
  }

  public async elasticSearchItemClicked(payload: TElasticSearchItemClickedPayload): Promise<void> {
    if (process.env.NODE_ENV !== 'development') {
      try {
        const wsClient = await wsConnectionPool.getAuthorizedClient(this.serviceName)
        await wsClient.call('my-connect', 'click', payload)
      } catch (e) {
        logger.error(e)
      }
    }
  }

  public checkEmailInAD = async (
    email: string
  ): Promise<{
    success: boolean
    data: Record<string, any>
  }> => {
    const wsClient = await wsConnectionPool.getAuthorizedClient(this.serviceName)
    try {
      const response = await wsClient.call('search', 'ad-info', { email })
      return { success: true, data: response.data }
    } catch (e) {
      return { success: false, data: {} }
    }
  }

  public toggleFavouriteByFlag = async (type: MY_CONNECT_TYPE, id: string, add: boolean) => {
    const wsClient = await wsConnectionPool.getAuthorizedClient(this.serviceName)

    let result
    try {
      result = await wsClient.call('favourite', 'toggle', { type, id, flag: add })
    } catch (e) {
      result = { status: 'error', message: 'System error' }
    }

    return result
  }
}

export const dialerAPI = new DialerApi()
